/**
 * Installing and loading a site's design system — the orchestration, over
 * injected stores, compiler, sandbox and API (so it runs under test).
 *
 * Install (owner): compile → load + conformance in the sandbox → store the
 * files locally → upload them and the manifest → switch the site's pointer on
 * the server. Last-known-good: nothing is activated unless every step before
 * it passed, so a bad upload leaves the current design system in place.
 *
 * Load (any device): read the server's pointer → get the manifest, bundle and
 * stylesheet (this device's OPFS first, else the server — hash-checked, then
 * kept locally) → compile (cached per content and compiler) → load + check in
 * the sandbox. A device that has it all works offline.
 */
import {
  assetKey,
  sha256Hex,
  type BlobStore,
} from "@website-builder/blob-store";
import { CONTRACT_VERSION } from "@website-builder/ds-contract";

import { COMPILATION_ID, type CompileResult } from "../dsSandbox/compile.js";
import type { ConformanceReport, DsFeature } from "../dsSandbox/conformance.js";
import type { DsSandbox } from "../dsSandbox/sandboxHost.js";
import { encodeManifest, parseManifest, type DsManifest } from "./manifest.js";

export interface DsServer {
  /** The site's active manifest hash (null = Duro). */
  getActive(): Promise<string | null>;
  /** Owner only; the server refuses anyone else. */
  setActive(manifestSha: string | null): Promise<void>;
  upload(sha: string, bytes: Uint8Array, contentType: string): Promise<void>;
  /** A blob from the server, or null. */
  fetch(sha: string): Promise<Uint8Array | null>;
}

export interface DsDeps {
  /** This device's store for the site (OPFS `sites/<siteId>`). */
  readonly local: BlobStore;
  readonly server: DsServer;
  readonly compile: (code: string, css: string) => Promise<CompileResult>;
  readonly createSandbox: () => Promise<DsSandbox>;
  readonly now?: () => Date;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

export type InstallResult =
  | { readonly ok: true; readonly manifestSha: string; readonly name: string }
  | {
      readonly ok: false;
      readonly stage:
        | "compile"
        | "load"
        | "conformance"
        | "upload"
        | "activate";
      readonly messages: readonly string[];
    };

/**
 * Load a release in a sandbox and check it. An UPLOAD must pass every check,
 * core and feature. LOADING an active release fails only on a core check:
 * one activated before a feature check existed keeps rendering everything
 * else, and the kinds it fails come back as `unsupported`.
 */
async function check(
  deps: DsDeps,
  js: string,
  mode: "upload" | "load",
): Promise<
  | {
      ok: true;
      sandbox: DsSandbox;
      name: string;
      unsupported: ReadonlySet<DsFeature>;
    }
  | { ok: false; stage: "load" | "conformance"; messages: string[] }
> {
  const sandbox = await deps.createSandbox();
  try {
    const loaded = await sandbox.load(js);
    const report: ConformanceReport = await sandbox.conform();
    const blocking =
      mode === "upload"
        ? [...report.failures, ...report.featureFailures.map((f) => f.message)]
        : [...report.failures];
    if (blocking.length > 0 || (mode === "upload" && !report.ok)) {
      sandbox.dispose();
      return {
        ok: false,
        stage: "conformance",
        messages: blocking.length ? blocking : ["conformance failed"],
      };
    }
    return {
      ok: true,
      sandbox,
      name: loaded.name,
      unsupported: new Set(report.featureFailures.map((f) => f.feature)),
    };
  } catch (e) {
    sandbox.dispose();
    return {
      ok: false,
      stage: "load",
      messages: [e instanceof Error ? e.message : String(e)],
    };
  }
}

const cacheKey = (bundleSha: string, cssSha: string) =>
  `_ds-cache/${bundleSha}-${cssSha}-${COMPILATION_ID}`;

export async function installDesignSystem(
  deps: DsDeps,
  upload: { readonly bundle: string; readonly css: string },
): Promise<InstallResult> {
  const compiled = await deps.compile(upload.bundle, upload.css);
  if (!compiled.ok)
    return { ok: false, stage: "compile", messages: compiled.errors };
  const checked = await check(deps, compiled.js, "upload");
  if (!checked.ok) return checked;
  checked.sandbox.dispose();

  const bundleBytes = enc.encode(upload.bundle);
  const cssBytes = enc.encode(upload.css);
  const bundleSha = await sha256Hex(bundleBytes.buffer as ArrayBuffer);
  const cssSha = await sha256Hex(cssBytes.buffer as ArrayBuffer);
  const manifest: DsManifest = {
    contractVersion: CONTRACT_VERSION,
    name: checked.name,
    bundleSha,
    cssSha,
    uploadedAt: (deps.now?.() ?? new Date()).toISOString(),
  };
  const manifestBytes = encodeManifest(manifest);
  const manifestSha = await sha256Hex(manifestBytes.buffer as ArrayBuffer);

  // Local first: this device can use it immediately, even before the upload.
  await deps.local.put(assetKey(bundleSha), bundleBytes);
  await deps.local.put(assetKey(cssSha), cssBytes);
  await deps.local.put(assetKey(manifestSha), manifestBytes);
  await deps.local.put(
    cacheKey(bundleSha, cssSha),
    enc.encode(JSON.stringify({ js: compiled.js, css: compiled.css })),
  );

  try {
    await deps.server.upload(bundleSha, bundleBytes, "text/javascript");
    await deps.server.upload(cssSha, cssBytes, "text/css");
    await deps.server.upload(manifestSha, manifestBytes, "application/json");
  } catch (e) {
    return {
      ok: false,
      stage: "upload",
      messages: [e instanceof Error ? e.message : String(e)],
    };
  }
  try {
    await deps.server.setActive(manifestSha);
  } catch (e) {
    return {
      ok: false,
      stage: "activate",
      messages: [e instanceof Error ? e.message : String(e)],
    };
  }
  return { ok: true, manifestSha, name: manifest.name };
}

/** A blob by hash: this device's copy, else the server's, hash-checked. */
async function blob(deps: DsDeps, sha: string): Promise<Uint8Array | null> {
  const local = await deps.local.get(assetKey(sha));
  if (local) return local;
  const remote = await deps.server.fetch(sha);
  if (!remote) return null;
  if ((await sha256Hex(remote.slice().buffer as ArrayBuffer)) !== sha)
    return null;
  await deps.local.put(assetKey(sha), remote);
  return remote;
}

export type ActiveDesignSystem =
  | { readonly kind: "duro" }
  | {
      readonly kind: "custom";
      readonly manifestSha: string;
      readonly manifest: DsManifest;
      /** The compiled stylesheet (what pages link as ds.css). */
      readonly css: string;
      readonly sandbox: DsSandbox;
      /** Widget kinds this release can't render (it predates their checks). */
      readonly unsupported: ReadonlySet<DsFeature>;
    }
  /** Not run on this device: its viewer hasn't agreed to this release yet. */
  | {
      readonly kind: "needs-consent";
      readonly manifestSha: string;
      readonly manifest: DsManifest;
    }
  | { readonly kind: "unavailable"; readonly reason: string };

export async function loadActiveDesignSystem(
  deps: DsDeps,
  /** Whether this viewer agreed to run this release (default: yes). Asked
   *  before anything of it is compiled or run. */
  mayRun: (manifestSha: string) => boolean = () => true,
): Promise<ActiveDesignSystem> {
  const manifestSha = await deps.server.getActive();
  if (manifestSha === null) return { kind: "duro" };
  const manifestBytes = await blob(deps, manifestSha);
  const manifest = manifestBytes ? parseManifest(manifestBytes) : null;
  if (!manifest)
    return { kind: "unavailable", reason: "its manifest can't be fetched" };
  if (!mayRun(manifestSha))
    return { kind: "needs-consent", manifestSha, manifest };

  let compiled: { js: string; css: string } | null = null;
  const cached = await deps.local.get(
    cacheKey(manifest.bundleSha, manifest.cssSha),
  );
  if (cached) compiled = JSON.parse(dec.decode(cached)) as typeof compiled;
  if (!compiled) {
    const bundle = await blob(deps, manifest.bundleSha);
    const css = await blob(deps, manifest.cssSha);
    if (!bundle || !css)
      return { kind: "unavailable", reason: "its files can't be fetched" };
    const result = await deps.compile(dec.decode(bundle), dec.decode(css));
    if (!result.ok)
      return { kind: "unavailable", reason: result.errors.join("; ") };
    compiled = { js: result.js, css: result.css };
    await deps.local.put(
      cacheKey(manifest.bundleSha, manifest.cssSha),
      enc.encode(JSON.stringify(compiled)),
    );
  }
  const checked = await check(deps, compiled!.js, "load");
  if (!checked.ok)
    return { kind: "unavailable", reason: checked.messages.join("; ") };
  return {
    kind: "custom",
    manifestSha,
    manifest,
    css: compiled!.css,
    sandbox: checked.sandbox,
    unsupported: checked.unsupported,
  };
}
